import PartyPackageCard from './PartyPackageCard';
import PartyPackageCardSkeleton from './PartyPackageCardSkeleton';
import hero from "./image/food-img.png";
import './PartyPackageListingPage.css';
import searchIcon from './image/search-icon.svg';
import { useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FiSettings } from "react-icons/fi";
import { FaHeart, FaUsers, FaRupeeSign, FaHistory, FaStar } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { RxDotFilled } from "react-icons/rx";
import FilterBar from './FilterBar';
import FilterModal from './FilterModel';
import { API_BASE_URL } from '../utils';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import BidFilterCarousel from './comp/BidFilterCarousel';
import BidFilterComp from './comp/BidFilterComp';
import { set } from 'date-fns';
import { useGeoLocation } from '../context/LocationContext';
import { useQuery } from '@tanstack/react-query';
import PackageFilterCarousel from './comp/PackageFilterCarousel';
import PackageCardNew from './comp/PackageCardNew';
import BidNewCardSkeleton from './comp/BidNewCardSkeleton';
import HeaderTitlewithBack from '../TopTitle/HeaderTitlewithBack';

const filters = [
  {
    label: "Review",
    type: "dropdown",
    icon: "IoIosArrowDown",
    options: ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"]
  },
  {
    label: "Distance",
    type: "dropdown",
    icon: "IoIosArrowDown",
    options: ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"]
  },
];

export default function PartyPackageListingPage() {
  const { latitude, longitude } = useGeoLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const loaderRef = useRef(null);

  const [filtersAndSort, setFiltersAndSort] = useState({
    selectedDropdowns: {},
    selectedPills: [],
  });
  const [showFilter, setShowFilter] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [filterItem, setFilterItem] = useState([]);

  const pathname = location.pathname;
  const { id, city, title, slug } = params;

  const cityParams = city?.replace(/-/g, " ") || "";
  const titleParams = title?.replace(/-/g, " ") || "";
  let finalCity = cityParams;
  let finalTitle = titleParams;

  const fetchPackageTitles = async () => {
    const response = await axios.get(`${API_BASE_URL}/master/APIs/Common/getAllPackageTitle`);
    return response.data.data;
  };

  const { data: packageTitleAndId = [] } = useQuery({
    queryKey: ['packageTitles'],
    queryFn: fetchPackageTitles,
    staleTime: 1000 * 60 * 60 * 5,
    cacheTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  if (slug) {
    const slugParams = slug.replace(/-/g, " ");
    const isPackageTitle = packageTitleAndId.find(
      (item) => item?.name && item.name.toLowerCase().trim() === slugParams.toLowerCase().trim()
    );
    if (isPackageTitle) finalTitle = slugParams;
    else finalCity = slugParams;
  }

  let readableTitle = "Party Packages";
  if (id) readableTitle = id.replace(/-/g, " ");
  else if (finalTitle) readableTitle = finalTitle + " Packages";
  const pageName = readableTitle;

  const fetchPackagesByTitleCity = async ({ queryKey }) => {
    const [, latitude, longitude, city = "", title = ""] = queryKey;
    const formData = new FormData();
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);
    formData.append("city", city);
    formData.append("title", title);

    const response = await axios.post(
      `${API_BASE_URL}/master/APIs/ClubPackage/getPackagesByCityAndTitle`,
      formData
    );

    if (!response.data.status) throw new Error("Failed to fetch packages");
    return response.data.data.slice(0, 40);
  };

  const fetchAllPackages = async ({ queryKey }) => {
    const [, latitude, longitude] = queryKey;
    const formData = new FormData();
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);
    formData.append("venue_id", "");
    formData.append("title_id", "");
    formData.append("keyword", "");

    const response = await axios.post(`${API_BASE_URL}/master/APIs/ClubPackage/allPackages`, formData);
    if (!response.data.status) throw new Error("Failed to fetch all packages");
    return response.data.data.slice(0, 40);
  };

  const cityOrTitleAvailable = !!finalCity || !!finalTitle;
  const { data = [], isLoading } = useQuery({
    queryKey: cityOrTitleAvailable
      ? ['packages', latitude, longitude, finalCity || '', finalTitle || '']
      : ['allPackages', latitude, longitude],
    queryFn: cityOrTitleAvailable
      ? () => fetchPackagesByTitleCity({ queryKey: ['packages', latitude, longitude, finalCity, finalTitle] })
      : () => fetchAllPackages({ queryKey: ['allPackages', latitude, longitude] }),
    enabled: !!latitude && !!longitude,
    staleTime: 1000 * 60 * 60 * 5,
    cacheTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });

  // ✅ Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisibleCount((prev) => prev + 6);
    }, { threshold: 1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, []);

  // ✅ Load filters from URL once
  useEffect(() => {
    const review = searchParams.get("review");
    const distance = searchParams.get("distance");
    const pillsParam = searchParams.get("filter");
    const pills = pillsParam ? pillsParam.split(",") : [];
    if (review || distance || pills.length > 0) {
      setFiltersAndSort({
        selectedDropdowns: {
          ...(review && { Review: [review] }),
          ...(distance && { Distance: distance }),
        },
        selectedPills: pills,
      });
    }
  }, []);

  // ✅ Sync filters to URL (no infinite re-renders)
  useEffect(() => {
    const params = new URLSearchParams();
    const { selectedDropdowns, selectedPills } = filtersAndSort;

    if (selectedDropdowns?.Review?.length > 0)
      params.set("review", selectedDropdowns.Review[0]);
    if (selectedDropdowns?.Distance) {
      const distanceValue = Array.isArray(selectedDropdowns.Distance)
        ? selectedDropdowns.Distance[0]
        : selectedDropdowns.Distance;
      params.set("distance", distanceValue);
    }
    if (selectedPills?.length > 0)
      params.set("filter", selectedPills.join(","));

    setSearchParams(params);
  }, [JSON.stringify(filtersAndSort)]);

  // ✅ Compute filterWiseCount
  const filterWiseCount = useMemo(() => {
    const counts = {};
    const packages = data || [];

    const distanceOptions = ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"];
    distanceOptions.forEach((opt) => {
      const distanceValue = parseInt(opt.match(/\d+/)?.[0] || "0", 10);
      counts[opt] = packages.filter((p) => parseFloat(p.distance) <= distanceValue).length;
    });

    const reviewOptions = ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"];
    reviewOptions.forEach((opt) => {
      const selectedReview = parseInt(opt);
      counts[opt] = packages.filter((p) => {
        const rating = parseFloat(p.totalAverageRating);
        return !isNaN(rating) && rating >= selectedReview - 0.4 && rating < selectedReview + 0.5;
      }).length;
    });

    return counts;
  }, [data]);

  // ✅ Apply filters locally
  useEffect(() => {
    let isFilterApplied = false;

    const filteredData = data.filter((item) => {
      if (filtersAndSort.selectedDropdowns?.Review) {
        isFilterApplied = true;
        const selectedReview = parseInt(filtersAndSort.selectedDropdowns.Review[0]);
        const rating = parseFloat(item.totalAverageRating);
        if (isNaN(rating) || rating < selectedReview - 0.4 || rating >= selectedReview + 0.5)
          return false;
      }

      if (filtersAndSort.selectedDropdowns?.Distance) {
        isFilterApplied = true;
        const rawDistance = filtersAndSort.selectedDropdowns.Distance;
        const selectedValue = Array.isArray(rawDistance) ? rawDistance[0] : rawDistance;
        const selectedDistance = parseInt(selectedValue.match(/\d+/)?.[0] || "0", 10);
        const itemDistance = parseFloat(item.distance);
        if (isNaN(itemDistance) || itemDistance > selectedDistance)
          return false;
      }

      return true;
    });

    setFilterItem(isFilterApplied ? filteredData : []);
  }, [data, filtersAndSort]);

  const capitalize = (str = "") => str.charAt(0).toUpperCase() + str.slice(1);


  return (
    <>
      <div className='party-package-list'>
        <div className='party-package-page-filter-container'>
          <HeaderTitlewithBack title={capitalize(pageName)} />
          <div className='party-package-page-filter'>
            <div className='party-package-page-filter-title-box'>
              <div className='pppftb-filter'>
                <FilterBar
                  filterList={filters}
                  filtersAndSort={filtersAndSort}
                  setFiltersAndSort={setFiltersAndSort}
                  setShowFilter={setShowFilter}
                  filterWiseCount={filterWiseCount}
                />
              </div>
            </div>
          </div>
        </div>

        {filterItem.length > 0 && (
          <div className='bid-listing-page-new-header-crousale'>
            <PackageFilterCarousel data={filterItem} />
          </div>
        )}

        <div className='party-package-page-container'>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <BidNewCardSkeleton key={index} />
            ))
          ) : (
            (filterItem.length > 0 ? filterItem : data)
              .slice(0, visibleCount)
              .map((item, index) => (
                <PackageCardNew
                  key={item.id || index}
                  data={item}
                  isListingPage={true}
                  isVenueList={false}
                  latitude={latitude}
                  longitude={longitude}
                />
              ))
          )}
          <div ref={loaderRef} style={{ height: "1px" }} />
        </div>
      </div>
      <div className='party-package-page-design-bottom-footer-tags-section'>
        <div className='party-package-page-detail-page-design-bottom-footer-tags'>
          <div className='party-package-page-detail-page-design-bottom-footer-tags-text'>
            <h1> Discover Top Party Venues & Group Booking Options in Delhi NCR</h1>
            <p>Make every celebration unforgettable with PartyWitty’s top-rated venues in Delhi, Noida, and Gurgaon. Whether you're planning a birthday, corporate party, kitty celebration, or weekend hangout, find the best bars, cafés, lounges, and private spaces to suit your vibe. Explore rooftop lounges, themed halls, and live music spots — all curated for your event type. Compare party packages, check availability, and book instantly. Enjoy exclusive offers for group bookings, couple-friendly venues, and custom food & drink options. Celebrate smart with PartyWitty — your trusted party planning partner in Delhi NCR.</p>
          </div>
          <div className='party-package-page-detail-page-design-bottom-footer-tags-text'>
            <h1>Book Party Tables Instantly with Custom Deals Across Delhi NCR</h1>
            <p>Looking to host a group celebration or birthday night out? Instantly book tables at the best clubs, restaurants, and lounges in Delhi NCR with PartyWitty. Explore verified venues with curated packages designed for birthdays, anniversaries, or casual get-togethers. Compare offers, check real-time availability, and unlock up to 50% off on group reservations. Whether it's a rooftop vibe, BYOB spot, or vibrant dance floor — book your ideal venue in minutes. No calls, no hassle — just instant access to the best party spots in town.</p>
          </div>
          <div className='party-package-page-detail-page-design-bottom-footer-tags-text'>
            <h1>Best Birthday & Group Party Packages in Delhi NCR – Book Now!</h1>
            <p>Planning a birthday bash or a group gathering? PartyWitty brings you the best party packages across Delhi, Noida, and Gurgaon — all in one place. From rooftop lounges to private clubs and BYOB venues, find curated packages with custom food, drink, and music options. Get up to 50% off on exclusive group deals, compare verified listings, and enjoy real-time booking convenience. Whether it's an intimate dinner or a large-scale celebration, PartyWitty makes planning stress-free and budget-friendly. Book your perfect party today with PartyWitty!</p>
          </div>
        </div>
      </div>


      <FilterModal
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        setFiltersAndSort={setFiltersAndSort}
        fetchDataWithFilters={data}
      />
    </>
  );
}
